import React, { useEffect, useRef, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import "./index.css";
import { GitHubCalendar } from "react-github-calendar";
import { LazyVideo } from "./components/ui/LazyVideo";
import { RetroArt } from "./components/ui/RetroArt";
import { PrinterSign, PrinterMachine, Iris } from "./components/Printer";
import { glitch, win, confetti } from "./fx";
import {
  SunIcon,
  MoonIcon,
  MailIcon,
  FileTextIcon,
  GitHubIcon,
  LinkedInIcon,
  TwitterIcon,
  CopyIcon,
  CheckIcon,
  ChevronDownIcon,
  ArrowUpRightIcon,
  QuoteIcon,
  XIcon,
  CircleNotchIcon,
  CircleHalfIcon,
  VerifiedIcon,
  ChevronLeftIcon,
} from "./components/Icons";
import {
  EMAIL,
  RESUME,
  GITHUB_USER,
  SOCIALS,
  EXPERIENCE,
  EDUCATION,
  PROJECTS,
  SKILLS,
  BOOKS,
  QUOTE,
  type Experience,
  type Project,
} from "./data";
import avatar from "./assets/avatar.jpg";
import avatarPixel from "./assets/avatar-pixel.jpg";
import portrait from "./assets/profile.jpg";

/* ---------------- shared styles ---------------- */

const btn = "group overflow-hidden inline-flex items-center gap-2 h-9 px-3 rounded-[6px] text-[15px] font-medium transition-colors";
/** icon that wipes in from the top while its button is hovered */
const wipe = "inline-flex group-hover:animate-[slideDown_0.5s_ease-out]";
const btnPrimary = `${btn} bg-(--fg) text-(--bg) hover:bg-(--title)`;
const btnGhost = `${btn} border border-(--border) bg-(--surface) text-(--title) hover:bg-(--hover)`;
const chip = "inline-flex items-center gap-2 h-8 px-2.5 rounded-[6px] border border-(--border) bg-(--surface) text-[14.5px] text-(--fg)";
const link = "inline-flex items-center gap-1 text-[15px] text-(--fg) hover:text-(--title) transition-colors";

const SOCIAL_ICON = { github: <GitHubIcon />, linkedin: <LinkedInIcon />, x: <TwitterIcon /> };

const ROLES = ["Web & Mobile Developer", "Founder @ TapRush", "React & React Native", "CS @ BITS Pilani"];
const ALIAS = "sansss";

const CAL_THEME = {
  dark: ["#1c1b18", "#3a3936", "#62615d", "#9a9994", "#e4e4e4"],
  light: ["#ececec", "#c9c9c9", "#9e9e9e", "#6b6b6b", "#2a2a2a"],
};

/* ---------------- pieces ---------------- */

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="rule">
    <h2 className="px-3 sm:px-4 py-2.5 text-[1.15rem] font-bold text-(--title)">{title}</h2>
    <div className="rule">{children}</div>
  </section>
);

const ViewAll: React.FC<{ onOpen: () => void }> = ({ onOpen }) => (
  <div className="rule flex justify-center py-3">
    <a
      href="/projects"
      onClick={(e) => {
        e.preventDefault();
        onOpen();
      }}
      className="rounded-[8px] border border-(--border) p-0.5"
    >
      <span className={`${btnPrimary} h-8`}>
        View All
        <span className="flex transition-transform duration-300 group-hover:scale-125">
          <ArrowUpRightIcon />
        </span>
      </span>
    </a>
  </div>
);

const Banner = () => (
  <div className="p-2 sm:p-3">
    <div className="bg-dot-grid h-28 sm:h-[200px]" />
  </div>
);

const Footer = () => (
  <footer className="rule p-2 sm:p-3">
    <div className="bg-dot-grid h-24 grid place-items-center">
      <span className="px-2 bg-(--bg) text-[13px] text-(--muted)">© 2026 Sanskriti Singh</span>
    </div>
  </footer>
);

const Logo: React.FC<{ text: string; src?: string }> = ({ text, src }) => (
  <span className="shrink-0 rounded-[9px] border border-(--border) p-0.5">
    {src ? (
      <img src={src} alt="" className="block size-10 rounded-[7px] object-cover" />
    ) : (
      <span className="grid place-items-center size-10 rounded-[7px] bg-white text-black text-[14px] font-bold tracking-tight">
        {text}
      </span>
    )}
  </span>
);

const ExperienceRow: React.FC<{ e: Experience }> = ({ e }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="group">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full text-left flex items-center gap-3 px-3 sm:px-4 py-3.5 cursor-pointer"
      >
        <Logo text={e.logo} src={e.logoSrc} />
        <div className="flex-1 min-w-0">
          <div className="text-[1.15rem] font-bold leading-tight text-(--title)">{e.company}</div>
          <div className="text-[15px] text-(--muted)">{e.role}</div>
          <div className="sm:hidden text-[14px] text-(--muted)">{e.period}</div>
        </div>
        <div className="hidden sm:block text-right">
          <div className="text-[15px] font-medium text-(--title)">{e.period}</div>
          <div className="text-[14.5px] text-(--muted)">{e.location}</div>
        </div>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="hidden sm:flex shrink-0 self-start mt-1.5 text-(--dim) group-hover:text-(--title) transition-colors duration-300"
        >
          <ChevronDownIcon />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="px-3 sm:px-4 pb-5 sm:pl-[72px]">
              <ul className="list-disc pl-4 space-y-1.5 text-[15px] leading-relaxed text-(--fg) marker:text-(--muted)">
                {e.points.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
              {e.stack && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {e.stack.map((t) => (
                    <span key={t} className={`${chip} h-7 text-[13.5px]`}>
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ProjectCard: React.FC<{ p: Project }> = ({ p }) => {
  const href = p.live ?? p.github;
  const media = "w-full h-full object-cover object-top";
  return (
    <article className="group flex flex-col">
      <a href={href} target="_blank" rel="noopener noreferrer" className="block rounded-[10px] border border-(--border) p-1">
        <div className="relative h-[200px] rounded-[6px] border border-(--border) bg-(--surface) overflow-hidden select-none">
          {/* artwork fades in on hover, like the reference */}
          <div
            data-art
            className="absolute inset-0 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-300"
          >
            <RetroArt art={p.art} />
          </div>
          <span className="absolute top-2 left-2.5 text-[13px] font-medium text-(--muted) transition-all duration-300 group-hover:left-1/2 group-hover:-translate-x-1/2 group-hover:text-black group-focus-within:left-1/2 group-focus-within:-translate-x-1/2 group-focus-within:text-black">
            {p.name}
          </span>
          <div
            className={`absolute bottom-0 left-1/2 -translate-x-1/2 ${p.portrait ? "w-[36%]" : "w-[80%]"} h-[75%] group-hover:h-[70%] group-focus-within:h-[70%] transition-all duration-300 rounded-t-[6px] bg-(--bg) p-[2px] pb-0`}
          >
            <div className="w-full h-full rounded-t-[4px] overflow-hidden">
              <LazyVideo src={p.video} poster={p.poster} className={media} />
            </div>
          </div>
        </div>
      </a>
      <div className="px-1 pt-3">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-[1.15rem] font-bold text-(--title)">{p.name}</h3>
          <span className="inline-flex items-center gap-1.5 text-[14px] text-(--muted) whitespace-nowrap">
            <span className="relative grid place-items-center size-3" aria-hidden="true">
              <span className="absolute size-2.5 rounded-full bg-(--live) opacity-40 animate-ping group-hover:hidden" />
              <span className="relative size-2 rounded-full bg-(--live)" />
            </span>
            {p.status}
          </span>
        </div>
        <p className="mt-1 text-[15px] leading-snug text-(--muted)">{p.description}</p>
        <div className="mt-2 flex gap-4">
          <a href={href} target="_blank" rel="noopener noreferrer" className={link}>
            View Project <ArrowUpRightIcon />
          </a>
          {p.live && p.github && (
            <a href={p.github} target="_blank" rel="noopener noreferrer" className={link}>
              Source <ArrowUpRightIcon />
            </a>
          )}
        </div>
      </div>
    </article>
  );
};

/** Two-up project grid split by dashed lines, like the reference. */
const ProjectGrid: React.FC<{ projects: Project[] }> = ({ projects }) => {
  const rows: Project[][] = [];
  for (let i = 0; i < projects.length; i += 2) rows.push(projects.slice(i, i + 2));
  return (
    <div className="relative">
      <div className="dash-v absolute left-1/2 top-0 h-full w-px -translate-x-1/2 hidden sm:block" aria-hidden="true" />
      <div className="rows">
        {rows.map((row) => (
          <div key={row[0]!.name} className="cells grid grid-cols-1 sm:grid-cols-2">
            {row.map((p) => (
              <div key={p.name} className="p-3 sm:p-4">
                <ProjectCard p={p} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------------- header pieces ---------------- */

const ThemeButton: React.FC<{ light: boolean; onClick: () => void; className?: string }> = ({ light, onClick, className = "" }) => (
  <button
    onClick={onClick}
    aria-label={light ? "Switch to dark theme" : "Switch to light theme"}
    className={`grid place-items-center size-8 rounded-[6px] border border-transparent transition-colors cursor-pointer ${className}`}
  >
    {light ? <MoonIcon /> : <SunIcon />}
  </button>
);

/** Cycles through titles with a soft blur cross-fade. */
const RotatingText: React.FC<{ texts: string[] }> = ({ texts }) => {
  const [i, setI] = useState(0);
  const moved = useRef(false);
  useEffect(() => {
    const id = setInterval(() => {
      moved.current = true;
      setI((n) => (n + 1) % texts.length);
    }, 3000);
    return () => clearInterval(id);
  }, [texts.length]);
  return (
    <div className="relative inline-block">
      <AnimatePresence mode="wait">
        <motion.span
          key={i}
          initial={moved.current ? { opacity: 0, filter: "blur(2px)" } : false}
          animate={{ opacity: 1, filter: "blur(0px)" }}
          exit={{ opacity: 0, filter: "blur(2px)" }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className="block text-[15px] text-(--muted)"
        >
          {texts[i]}
        </motion.span>
      </AnimatePresence>
    </div>
  );
};

/** Full-size photo over a dimmed backdrop; Esc or a click outside closes it. */
const Lightbox: React.FC<{ open: boolean; src: string; pixel: boolean; onClose: () => void }> = ({ open, src, pixel, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    const gutter = innerWidth - document.documentElement.clientWidth;
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${gutter}px`;
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [open, onClose]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onClose}
          className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/80 cursor-pointer"
        >
          <button
            onClick={onClose}
            aria-label="Close"
            className="fixed top-4 right-4 z-[1101] grid place-items-center size-10 rounded-full bg-zinc-800 text-white hover:bg-zinc-700 transition cursor-pointer"
          >
            <XIcon />
          </button>
          <motion.img
            src={src}
            alt="Sanskriti Singh"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            className={`max-h-[85vh] w-auto max-w-[min(450px,100%)] rounded-[10px] cursor-default select-none ${pixel ? "aspect-square w-[min(450px,100%)] [image-rendering:pixelated]" : ""}`}
            draggable={false}
          />
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

/* ---------------- page ---------------- */

interface PageProps {
  light: boolean;
  inMachine: boolean;
  onToggleTheme: () => void;
}

const HomePage: React.FC<PageProps & { onViewAll: () => void }> = ({ light, inMachine, onToggleTheme, onViewAll }) => {
  const [now, setNow] = useState(() => new Date());
  const [copied, setCopied] = useState(false);
  const [alias, setAlias] = useState(false);
  const [glitching, setGlitching] = useState(false);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  const istTime = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(now);

  const flipAlias = () => {
    glitch();
    setGlitching(true);
    setAlias((a) => !a);
    setTimeout(() => setGlitching(false), 700);
  };

  const copyEmail = async (e: React.MouseEvent<HTMLButtonElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    try {
      await navigator.clipboard.writeText(EMAIL);
      setCopied(true);
      win();
      confetti(r.left + r.width / 2, r.top + r.height / 2);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.location.href = `mailto:${EMAIL}`;
    }
  };

  return (
    <div className="rail max-w-[690px] mx-auto min-h-screen">
      <Banner />

      {/* Header */}
      <header className="rule flex gap-3 px-3 sm:px-4 py-3">
        <button
          onClick={() => setLightbox(true)}
          aria-label="View photo"
          className="shrink-0 rounded-[12px] border border-(--border) p-1 cursor-pointer hover:brightness-90 transition duration-300"
        >
          <img
            src={alias ? avatarPixel : avatar}
            alt="Sanskriti Singh"
            draggable={false}
            className={`size-[76px] sm:size-[88px] rounded-[8px] object-cover select-none ${alias ? "[image-rendering:pixelated]" : ""} ${glitching ? "glitch-image" : ""}`}
          />
        </button>
        <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5 select-none">
          <button onClick={flipAlias} aria-label="Switch persona" className="mt-1 w-fit cursor-pointer">
            {alias ? (
              <CircleHalfIcon className="text-(--fg)" />
            ) : (
              <CircleNotchIcon className="text-(--dim) rotate-120 hover:text-(--fg) transition-all duration-300" />
            )}
          </button>
          <div>
            <h1 className="text-[1.45rem] sm:text-[1.55rem] font-bold leading-[1.08] text-(--title)">
              <span className={glitching ? "glitch-text" : ""}>
                {alias ? (
                  <span className="inline-flex items-center gap-0.5">
                    {ALIAS}
                    <VerifiedIcon className="text-blue-500 mt-1" />
                  </span>
                ) : (
                  "Sanskriti Singh"
                )}
              </span>
            </h1>
            <RotatingText texts={ROLES} />
          </div>
        </div>
        <div className="flex flex-col items-end justify-between py-0.5">
          {inMachine ? (
            <span />
          ) : (
            <ThemeButton light={light} onClick={onToggleTheme} className="-mr-1.5 text-(--dim) hover:text-(--muted)" />
          )}
          <span className="hidden sm:inline text-[14px] text-(--muted) tabular-nums whitespace-nowrap">{istTime} IST</span>
        </div>
      </header>
      <Lightbox open={lightbox} src={alias ? avatarPixel : portrait} pixel={alias} onClose={() => setLightbox(false)} />

        {/* Intro */}
        <div className="rule px-3 sm:px-4 pt-4 pb-5">
          <div className="space-y-3 text-[1.05rem] leading-[1.6] text-(--fg)">
            <p>
              Hey, I'm Sanskriti, a web & mobile developer who loves shipping fast, polished products
              end to end: React and Next.js on the web, Swift, React Native and Expo on mobile, and the
              APIs that power them.
            </p>
            <p>
              I've built and shipped multiple mobile apps to production, owning each one end to end,
              from the first build through App Store release to keeping it running smoothly for real
              users. My latest,{" "}
              <a
                href="https://apps.apple.com/in/app/suzi/id6760543806"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-(--title) underline decoration-(--border) underline-offset-4 hover:decoration-(--title) transition-colors"
              >
                Suzi
              </a>
              , a self-custodial crypto wallet, is live on the App Store. Based in Delhi, India, and
              always open to interesting problems and new opportunities.
            </p>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <a href={`mailto:${EMAIL}`} className={btnPrimary}>
              <span className={wipe}>
                <MailIcon />
              </span>
              Send an email
            </a>
            <a href={RESUME} target="_blank" rel="noopener noreferrer" className={btnGhost}>
              <span className={wipe}>
                <FileTextIcon />
              </span>
              Resume
            </a>
          </div>

          <p className="mt-6 text-[1.05rem] text-(--fg)">
            Here are my <span className="font-bold text-(--title)">socials</span>
          </p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {SOCIALS.map((s) => (
              <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" className={`${chip} hover:bg-(--hover) transition-colors`}>
                {SOCIAL_ICON[s.icon]}
                {s.label}
              </a>
            ))}
          </div>

          <div className="mt-6 text-(--muted)">
            <GitHubCalendar
              username={GITHUB_USER}
              colorScheme={light ? "light" : "dark"}
              theme={CAL_THEME}
              blockSize={9}
              blockMargin={3}
              blockRadius={2}
              fontSize={12}
            />
          </div>
        </div>

        <Section title="Experiences">
          <div className="rows">
            {EXPERIENCE.map((e) => (
              <ExperienceRow key={e.company} e={e} />
            ))}
          </div>
        </Section>

        <Section title="Education">
          <div className="flex items-center gap-3 px-3 sm:px-4 py-3.5">
            <Logo text={EDUCATION.logo} src={EDUCATION.logoSrc} />
            <div className="flex-1 min-w-0">
              <div className="text-[1.15rem] font-bold leading-tight text-(--title)">{EDUCATION.school}</div>
              <div className="text-[15px] text-(--muted)">{EDUCATION.degree}</div>
            </div>
            <div className="text-right">
              <div className="text-[15px] font-medium text-(--title)">{EDUCATION.period}</div>
              <div className="hidden sm:block text-[14.5px] text-(--muted)">{EDUCATION.location}</div>
            </div>
          </div>
        </Section>

        <Section title="Projects">
          <ProjectGrid projects={PROJECTS.slice(0, 4)} />
          {PROJECTS.length > 4 && <ViewAll onOpen={onViewAll} />}
        </Section>

        <Section title="Skills & Technologies">
          <div className="flex flex-wrap justify-center gap-2 px-3 sm:px-4 py-4">
            {SKILLS.map(([name, slug]) => (
              <span key={name} className={chip}>
                {slug && (
                  <img
                    src={`https://cdn.simpleicons.org/${slug}/9f9fa9`}
                    alt=""
                    width={14}
                    height={14}
                    loading="lazy"
                    className="size-3.5"
                  />
                )}
                {name}
              </span>
            ))}
          </div>
        </Section>

        <Section title="Bookshelf">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 sm:p-4">
            {BOOKS.map((b) => (
              <div key={b.title} className="flex gap-3 rounded-[10px] border border-(--border) bg-(--surface) p-1.5">
                <img
                  src={b.cover}
                  alt={`${b.title} cover`}
                  loading="lazy"
                  className="w-14 aspect-[2/3] shrink-0 rounded-[6px] border border-(--border) object-cover"
                />
                <div className="flex flex-col min-w-0 py-1 pr-1">
                  <span className="text-[15.5px] font-bold leading-snug text-(--title)">{b.title}</span>
                  <span className="text-[14px] text-(--muted)">{b.author}</span>
                  <span className={`${chip} mt-auto self-start h-6 px-2 text-[12.5px] tabular-nums`}>{b.year}</span>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Get in touch">
          <div className="p-3 sm:p-4">
            <p className="text-[15px] text-(--muted) mb-3">
              For roles, collaborations, or general enquiries, email is the best way to reach me.
            </p>
            <div className="bg-striped rounded-[10px] border border-(--border) p-3 flex gap-2">
              <a
                href={`mailto:${EMAIL}`}
                className="flex-1 min-w-0 h-9 px-3 flex items-center rounded-[6px] border border-(--border) bg-(--bg) text-[15px] text-(--fg) hover:text-(--title) truncate"
              >
                {EMAIL}
              </a>
              <button onClick={copyEmail} className={`${btnPrimary} cursor-pointer shrink-0`}>
                <span className={wipe}>{copied ? <CheckIcon /> : <CopyIcon />}</span>
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        </Section>

        {/* Quote */}
        <figure className="rule px-6 py-12 text-center">
          <span className="inline-block text-(--border)">
            <QuoteIcon />
          </span>
          <blockquote className="mt-3 text-[1.35rem] sm:text-[1.55rem] font-bold italic leading-snug text-(--title)">
            "{QUOTE.text}"
          </blockquote>
          <figcaption className="mt-5 flex items-center justify-center gap-3 text-[13px] font-semibold uppercase tracking-[0.12em] text-(--muted)">
            <span className="w-10 h-px bg-(--border)" />
            {QUOTE.author}
            <span className="w-10 h-px bg-(--border)" />
          </figcaption>
        </figure>

        <Footer />
    </div>
  );
};

/** /projects — every project, same grid as the home page. */
const ProjectsPage: React.FC<PageProps & { onBack: () => void }> = ({ light, inMachine, onToggleTheme, onBack }) => (
  <div className="rail max-w-[690px] mx-auto min-h-screen">
    <Banner />
    <div className="rule flex items-center justify-between px-2 sm:px-3 py-2">
      <div className="flex items-center gap-2">
        <button
          onClick={onBack}
          aria-label="Go back"
          className="grid place-items-center size-8 rounded-[6px] border border-transparent text-(--title) hover:border-(--border) hover:bg-(--hover) transition-colors duration-300 cursor-pointer"
        >
          <ChevronLeftIcon />
        </button>
        <h1 className="text-[1.15rem] font-bold leading-tight text-(--title)">Projects</h1>
      </div>
      {!inMachine && (
        <ThemeButton
          light={light}
          onClick={onToggleTheme}
          className="text-(--muted) hover:text-(--fg) hover:bg-(--hover) hover:border-(--border)"
        />
      )}
    </div>
    <div className="rule">
      <ProjectGrid projects={PROJECTS} />
    </div>
    <Footer />
  </div>
);

/* ---------------- app ---------------- */

type Route = "/" | "/projects";
const toRoute = (path: string): Route => (path.startsWith("/projects") ? "/projects" : "/");

type ViewTransitionDoc = Document & { startViewTransition?: (cb: () => void) => { finished: Promise<void> } };

export function App() {
  const [light, setLight] = useState(() => document.documentElement.classList.contains("light"));
  const [route, setRoute] = useState<Route>(() => toRoute(location.pathname));
  const [printing, setPrinting] = useState(false);
  const [iris, setIris] = useState(1); // >0 plays the iris; the first one is the page-load reveal
  const switching = useRef(false);

  useEffect(() => {
    const onPop = () => {
      setRoute(toRoute(location.pathname));
      setIris((n) => n + 1);
    };
    addEventListener("popstate", onPop);
    return () => removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    document.title = route === "/projects" ? "Projects | Sanskriti Singh" : "Sanskriti Singh | Web & Mobile Developer";
  }, [route]);

  const navigate = (to: Route) => {
    if (to === route) return;
    history.pushState({ app: true }, "", to);
    setRoute(to);
    scrollTo(0, 0);
    setIris((n) => n + 1);
  };
  const back = () => (history.state?.app ? history.back() : navigate("/"));

  /** Wipe into the new theme: a straight edge travelling from the top of the screen to the bottom. */
  const toggleTheme = () => {
    const apply = () => {
      const next = !document.documentElement.classList.contains("light");
      document.documentElement.classList.toggle("light", next);
      try {
        localStorage.setItem("theme", next ? "light" : "dark");
      } catch {}
      flushSync(() => setLight(next));
    };
    const doc = document as ViewTransitionDoc;
    if (!doc.startViewTransition || switching.current || matchMedia("(prefers-reduced-motion: reduce)").matches) return apply();
    switching.current = true;
    doc.startViewTransition(apply).finished.finally(() => {
      switching.current = false;
    });
  };

  const pageProps = { light, inMachine: printing, onToggleTheme: toggleTheme };
  const page =
    route === "/projects" ? (
      <ProjectsPage {...pageProps} onBack={back} />
    ) : (
      <HomePage {...pageProps} onViewAll={() => navigate("/projects")} />
    );

  if (printing) {
    return (
      <PrinterMachine
        onClose={() => {
          setPrinting(false);
          setIris((n) => n + 1);
        }}
        themeToggle={
          <ThemeButton
            light={light}
            onClick={toggleTheme}
            className="absolute left-1 top-1 text-white/45 hover:text-white/85 hover:bg-white/10 hover:border-white/15"
          />
        }
      >
        {page}
      </PrinterMachine>
    );
  }

  return (
    <>
      {page}
      {iris > 0 && <Iris key={iris} onDone={() => setIris(0)} />}
      <PrinterSign onClick={() => setPrinting(true)} />
    </>
  );
}

export default App;
